import { batchUpdateProducts, deleteProduct, updateProduct } from '../services/productService'
import { getUserFacingErrorMessage } from '../utils/userFacingError'

export type BulkProductItem = {
  id: number
  name: string
}

export type BulkProductFailure = {
  id: number
  label: string
  message: string
}

export type BulkProductResult = {
  succeeded: number
  failed: BulkProductFailure[]
}

function collectResults(
  items: BulkProductItem[],
  settled: PromiseSettledResult<unknown>[],
): BulkProductResult {
  const failed: BulkProductFailure[] = []
  let succeeded = 0
  settled.forEach((result, index) => {
    const item = items[index]
    if (!item) return
    if (result.status === 'fulfilled') {
      succeeded += 1
      return
    }
    failed.push({
      id: item.id,
      label: item.name,
      message: getUserFacingErrorMessage(result.reason, 'Request failed'),
    })
  })
  return { succeeded, failed }
}

export async function bulkSetProductActive(
  items: BulkProductItem[],
  isActive: boolean,
): Promise<BulkProductResult> {
  if (items.length === 0) return { succeeded: 0, failed: [] }
  const settled = await Promise.allSettled(
    items.map((item) => updateProduct(item.id, { isActive })),
  )
  return collectResults(items, settled)
}

export async function bulkAssignProductCategory(
  items: BulkProductItem[],
  categorySlug: string,
): Promise<BulkProductResult> {
  if (items.length === 0) return { succeeded: 0, failed: [] }
  const slug = categorySlug.trim()
  const settled = await Promise.allSettled(
    items.map((item) => updateProduct(item.id, { categorySlug: slug || null })),
  )
  return collectResults(items, settled)
}

export async function bulkDeleteProducts(items: BulkProductItem[]): Promise<BulkProductResult> {
  if (items.length === 0) return { succeeded: 0, failed: [] }
  const settled = await Promise.allSettled(items.map((item) => deleteProduct(item.id)))
  return collectResults(items, settled)
}

export async function bulkUpdateProductPriceStock(
  items: BulkProductItem[],
  updates: { price?: number; stockQuantity?: number },
): Promise<BulkProductResult> {
  if (items.length === 0) return { succeeded: 0, failed: [] }
  const productIds = items.map((item) => item.id)
  const payload = { productIds, ...updates }
  const result = await batchUpdateProducts(payload)
  const nameById = new Map(items.map((item) => [item.id, item.name]))
  return {
    succeeded: result.succeeded,
    failed: result.failed.map((failure) => ({
      id: failure.id,
      label: nameById.get(failure.id) ?? `Product #${failure.id}`,
      message: failure.message,
    })),
  }
}

export function formatBulkResultMessage(
  actionLabel: string,
  result: BulkProductResult,
): { toast: string; variant: 'success' | 'info' | 'error' } {
  const { succeeded, failed } = result
  if (failed.length === 0) {
    return {
      toast: `${actionLabel}: ${succeeded} product${succeeded === 1 ? '' : 's'} updated.`,
      variant: 'success',
    }
  }
  if (succeeded === 0) {
    return {
      toast: `${actionLabel} failed for all ${failed.length} selected product${failed.length === 1 ? '' : 's'}.`,
      variant: 'error',
    }
  }
  return {
    toast: `${actionLabel}: ${succeeded} succeeded, ${failed.length} failed.`,
    variant: 'info',
  }
}
